import React, { FunctionComponent, useState } from 'react';
import { useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import { UPDATE_PORTFOLIO } from '../../graphql/UPDATE_PORTFOLIO';
import { DELETE_PORTFOLIO } from '../../graphql/DELETE_PORTFOLIO';
import Upload, { EUploadTypes, getShortFilename } from '../Upload';
import { Portfolio } from '../../interfaces/user';
import { Modal } from '../Modal';

interface PortfolioFormProps {
    onSuccess: () => void;
    onCancel: () => void;
    portfolio?: Portfolio;
}

interface MediaItem {
    id?: number;
    fileUrl: string;
}

const PortfolioForm: FunctionComponent<PortfolioFormProps> = ({
    onSuccess,
    onCancel,
    portfolio
}) => {
    const isEditMode = !!portfolio;

    const [title, setTitle] = useState(portfolio?.title ?? '');
    const [description, setDescription] = useState((portfolio?.description ?? '').substring(0, 500));
    const [videoUrl, setVideoUrl] = useState(portfolio?.videoUrl ?? portfolio?.url ?? '');
    const [role, setRole] = useState(portfolio?.teamMembers?.[0] ?? 'Director');
    const [selectedGenres, setSelectedGenres] = useState<string[]>(portfolio?.genres ?? []);

    const [savedMedia, setSavedMedia] = useState<MediaItem[]>(
        portfolio && portfolio.image
            ? [{ fileUrl: portfolio.image.includes('via.placeholder.com') ? 'https://placehold.co/800x450/1A202C/8A94A6?text=No+Thumbnail+Provided' : portfolio.image }]
            : []
    );
    const [deleteModalIsActive, setDeleteModalIsActive] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const [updatePortfolio, { loading: isSubmitting }] = useMutation(UPDATE_PORTFOLIO, {
        refetchQueries: ['GET_USER_PORTFOLIOS'],
        awaitRefetchQueries: true,
        onCompleted: () => {
            toast.success('Portfolio item updated successfully!');
            onSuccess();
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to update portfolio item');
        }
    });

    const [deletePortfolio] = useMutation(DELETE_PORTFOLIO, {
        refetchQueries: ['GET_USER_PORTFOLIOS'],
        awaitRefetchQueries: true,
        onCompleted: () => {
            toast.success('Portfolio item deleted successfully!');
            setIsDeleting(false);
            onSuccess();
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to delete portfolio item');
            setIsDeleting(false);
        }
    });

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!title.trim() || !description.trim()) {
            toast.error('Please fill in the required fields');
            return;
        }

        const data = {
            title: title.trim(),
            description: description.trim(),
            videoUrl: videoUrl.trim(),
            url: videoUrl.trim(),
            genres: selectedGenres,
            teamMembers: [role],
            image: savedMedia.length > 0 ? savedMedia[0].fileUrl : (portfolio?.image ?? 'https://placehold.co/800x450/1A202C/8A94A6?text=No+Thumbnail+Provided')
        };

        if (isEditMode && portfolio) {
            await updatePortfolio({
                variables: {
                    portfolioId: Number(portfolio.id),
                    data
                }
            });
        }
    };

    const handleDelete = async () => {
        if (!portfolio) return;
        setIsDeleting(true);
        setDeleteModalIsActive(false);
        await deletePortfolio({
            variables: { portfolioId: Number(portfolio.id) }
        });
    };

    return (
        <div className="portfolio-form add-work-modal" style={{ width: '100%', maxWidth: '600px', margin: '0 auto' }}>
            <div className="is-flex is-align-items-center" style={{ paddingBottom: '1.5rem', borderBottom: '1px solid #F1F5F9', marginBottom: '1.5rem' }}>
                <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
                    <button
                        onClick={onCancel}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 0 }}
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="#0F172A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                </div>
                <div style={{ flex: 1, textAlign: 'center', fontSize: '1.1rem', fontWeight: 700, color: '#0F172A', whiteSpace: 'nowrap' }}>
                    Edit Portfolio
                </div>
                <div style={{ flex: 1 }}></div>
            </div>

            <div className="form-content">
                {/* Thumbnail Upload */}
                <div className="field" style={{ marginBottom: '1.5rem' }}>
                    <label className="label" style={{ color: '#8A94A6', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>Thumbnail</label>
                    {savedMedia.length > 0 ? (
                        <div style={{ border: '1px solid #E2E8F0', borderRadius: '8px', padding: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', minWidth: 0, flex: 1 }}>
                                <div style={{ width: '48px', height: '48px', borderRadius: '4px', overflow: 'hidden', backgroundColor: '#F1F5F9', flexShrink: 0 }}>
                                    <img src={savedMedia[0].fileUrl} alt="Thumbnail" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                </div>
                                <div style={{ minWidth: 0, flex: 1 }}>
                                    <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#223141' }}>Image uploaded</div>
                                    <div style={{ fontSize: '0.8rem', color: '#8A94A6', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{getShortFilename(savedMedia[0].fileUrl)}</div>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setSavedMedia([])}
                                style={{ background: 'none', border: 'none', color: '#EF4444', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}
                            >
                                Remove
                            </button>
                        </div>
                    ) : (
                        <Upload
                            uploadContainer={() => (
                                <div style={{ width: '100%', border: '1px dashed #CBD5E1', borderRadius: '8px', backgroundColor: '#FFFFFF', padding: '3rem 0', cursor: 'pointer', transition: 'all 0.2s' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <span style={{ color: '#0F172A', fontSize: '0.95rem', fontWeight: 600 }}>Click or drag to upload image</span>
                                        <span style={{ color: '#94A3B8', fontSize: '0.85rem', marginTop: '0.25rem' }}>Maximum file size: 5MB</span>
                                    </div>
                                </div>
                            )}
                            uploadType={EUploadTypes.projectUpdate}
                            formType="edit"
                            medias={savedMedia}
                            setSavedMedia={(val: any) => {
                                if (typeof val === 'function') {
                                    const newMedia = val([]);
                                    if (newMedia.length > 0) setSavedMedia([newMedia[newMedia.length - 1]]);
                                } else {
                                    setSavedMedia(Array.isArray(val) ? val : [val]);
                                }
                            }}
                        />
                    )}
                </div>

                {/* Title */}
                <div className="field" style={{ marginBottom: '1.5rem' }}>
                    <label className="label" style={{ color: '#8A94A6', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                        Title <span style={{ color: '#EF4444' }}>*</span>
                    </label>
                    <div className="control">
                        <input className="input" type="text" placeholder="Portfolio item title" value={title} onChange={(e) => setTitle(e.target.value)} style={{ borderRadius: '8px', border: '1px solid #E2E8F0', backgroundColor: '#FFFFFF', boxShadow: 'none', height: '48px', color: '#223141', fontWeight: 400 }} disabled={isSubmitting} />
                    </div>
                </div>

                {/* Description */}
                <div className="field" style={{ marginBottom: '1.5rem' }}>
                    <label className="label" style={{ color: '#8A94A6', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                        Description <span style={{ color: '#EF4444' }}>*</span>
                    </label>
                    <div className="control" style={{ position: 'relative' }}>
                        <textarea className="textarea" placeholder="Tell us about this project..." value={description} onChange={(e) => setDescription(e.target.value)} rows={4} maxLength={500} style={{ borderRadius: '8px', border: '1px solid #E2E8F0', backgroundColor: '#FFFFFF', boxShadow: 'none', color: '#223141', fontWeight: 400, resize: 'none', paddingBottom: '24px' }} disabled={isSubmitting}></textarea>
                        <div style={{ position: 'absolute', bottom: '8px', right: '12px', fontSize: '0.75rem', color: '#94A3B8' }}>
                            {description.length}/500
                        </div>
                    </div>
                </div>

                {/* Role */}
                <div className="field" style={{ marginBottom: '1.5rem' }}>
                    <label className="label" style={{ color: '#8A94A6', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>Your role</label>
                    <div className="control">
                        <div className="select is-fullwidth">
                            <select value={role} onChange={(e) => setRole(e.target.value)} style={{ borderRadius: '8px', border: '1px solid #E2E8F0', backgroundColor: '#FFFFFF', boxShadow: 'none', height: '48px', color: '#223141', fontWeight: 400 }} disabled={isSubmitting}>
                                <option value="Director">Director</option>
                                <option value="Producer">Producer</option>
                                <option value="Editor">Editor</option>
                                <option value="Cinematographer">Cinematographer</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Video URL */}
                <div className="field" style={{ marginBottom: '1.5rem' }}>
                    <label className="label" style={{ color: '#8A94A6', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>Video URL</label>
                    <div className="control">
                        <input className="input" type="text" placeholder="https://example.com" value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} style={{ borderRadius: '8px', border: '1px solid #E2E8F0', backgroundColor: '#FFFFFF', boxShadow: 'none', height: '48px', color: '#223141', fontWeight: 400 }} disabled={isSubmitting} />
                    </div>
                </div>

                {/* Genres */}
                <div className="field" style={{ marginBottom: '2rem' }}>
                    <label className="label" style={{ color: '#8A94A6', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>Genres</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {Array.from(new Set(['Sci-Fi', 'Drama', 'Thriller', 'Action', 'Animation', 'Documentary', ...selectedGenres])).map(genre => {
                            const isSelected = selectedGenres.includes(genre);
                            return (
                                <button
                                    key={genre}
                                    type="button"
                                    onClick={() => {
                                        if (isSelected) {
                                            setSelectedGenres(selectedGenres.filter(g => g !== genre));
                                        } else {
                                            setSelectedGenres([...selectedGenres, genre]);
                                        }
                                    }}
                                    disabled={isSubmitting}
                                    style={{
                                        padding: '6px 14px',
                                        borderRadius: '24px',
                                        border: `1px solid ${isSelected ? '#6366F1' : '#E2E8F0'}`,
                                        backgroundColor: isSelected ? '#EEF2FF' : '#FFFFFF',
                                        color: isSelected ? '#6366F1' : '#475569',
                                        fontSize: '0.8rem',
                                        fontWeight: 600,
                                        cursor: isSubmitting ? 'default' : 'pointer',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    {genre}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Submit Button */}
                <button
                    className={`button is-fullwidth ${isSubmitting ? 'is-loading' : ''}`}
                    style={{ backgroundColor: '#5775FF', color: 'white', height: '48px', borderRadius: '24px', fontWeight: 600, fontSize: '1rem', border: 'none', marginBottom: '1.5rem' }}
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                >
                    Update Portfolio
                </button>

                {/* Delete Button */}
                <div style={{ textAlign: 'center' }}>
                    <button
                        className={`button is-danger is-outlined is-rounded ${isDeleting ? 'is-loading' : ''}`}
                        type="button"
                        onClick={() => setDeleteModalIsActive(true)}
                        disabled={isSubmitting || isDeleting}
                        style={{ fontSize: '0.9rem', fontWeight: 600, padding: '0 2rem' }}
                    >
                        Delete this portfolio item
                    </button>
                </div>
            </div>

            <Modal
                isOpen={deleteModalIsActive}
                onClose={() => setDeleteModalIsActive(false)}
            >
                <div style={{ padding: '1.5rem', textAlign: 'center' }}>
                    <strong className="m-b-2 is-block" style={{ fontSize: '1.1rem', color: '#0F172A', marginBottom: '1.5rem' }}>
                        Are you sure you want to delete this portfolio item?
                    </strong>
                    <div className="columns is-mobile">
                        <div className="column">
                            <button
                                type="button"
                                className="button is-fullwidth is-rounded"
                                onClick={() => setDeleteModalIsActive(false)}
                                style={{ height: '44px', fontWeight: 600 }}
                            >
                                Cancel
                            </button>
                        </div>
                        <div className="column">
                            <button
                                type="button"
                                className={`button is-danger is-fullwidth is-rounded ${isDeleting ? 'is-loading' : ''}`}
                                onClick={handleDelete}
                                disabled={isDeleting}
                                style={{ height: '44px', fontWeight: 600 }}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default PortfolioForm;